<script setup>
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import ClientBondService from '@/services/ClientBondService';
import DownloadButtonComponent from '@/components/documents/DownloadButtonComponent.vue';
import BondPurchaseOverlay from '@/components/bonds/BondPurchaseOverlay.vue';
import {formatAmount, formatDate, formatNumber} from '@/helper';

/**
 * Obsah detailu dluhopisu podle vzoru (_Docs/IMA-517-dluhopisy-prototyp.html, náhled 03): karty Parametry a Stav
 * vedle sebe, pod nimi Plán výplat (Budoucí / Všechny) a Dokumenty k emisi (aktuální verze).
 * Používá ho stránka detailu (/bonds/:id, /advisor/bonds/:id) i modal „Detail“ ve výběru dluhopisů v kalkulačce.
 *
 * Plán vlastněného dluhopisu je z ESO (sečtený přes všechny nákupy), u nevlastněného orientační na 1 ks
 * s čistým kupónem po srážkové dani a splacením jistiny v poslední výplatě.
 * Klient má u dluhopisu v prodeji tlačítko Koupit / Přikoupit (BondPurchaseOverlay); poradce místo vlastněných
 * kusů vidí počet svých klientů s dluhopisem (data z api/advisor/bonds).
 */
const props = defineProps({
  bondId: {type: [Number, String], required: true},
  // pohled poradce (api/advisor/bonds) – jinak klienta (api/client-report/bonds)
  advisor: {type: Boolean, default: false},
  // vložený detail (modal v onboardingu): bez tlačítka Koupit, náhled dokumentu v nové záložce
  embedded: {type: Boolean, default: false},
});
// loaded(bond) – stránka podle něj zobrazí název v záhlaví
const emit = defineEmits(['loaded']);

const {t} = useI18n();
const router = useRouter();
const bond = ref(null);
const loading = ref(true);
const notFound = ref(false);
const scheduleFilter = ref('future');
const isAdvisor = computed(() => props.advisor);
const purchaseOpen = ref(false);

/** Srážková daň z kupónu – napevno jako ve vzoru (ESO sazbu neposílá, čistý kupón v orientačním plánu s ní počítá). */
const WITHHOLDING_TAX_PCT = 15;

onMounted(loadBond);

function loadBond() {
  const request = isAdvisor.value ? ClientBondService.getAdvisorBond(props.bondId) : ClientBondService.getBond(props.bondId);
  return request.then(response => {
    bond.value = response;
    emit('loaded', response);
  }).catch(() => {
    // BE vrací 404 pro dluhopis, který klient nemá a není mu dostupný
    notFound.value = true;
  }).finally(() => {
    loading.value = false;
  });
}

/** Po nákupu se detail načte znovu – vlastněné kusy a plán výplat z vlastněných dluhopisů. */
function onPurchaseClose(purchased) {
  purchaseOpen.value = false;
  if (purchased) {
    loadBond();
  }
}

const date = value => (value ? formatDate(value, 'd.m.Y') : '---');

/** Parametry ve dvou sloupcích jako ve vzoru. */
const paramColumns = computed(() => {
  const b = bond.value;
  if (!b) {
    return [[], []];
  }
  return [
    [
      [t('clientBonds.isin'), b.isin ?? '---'],
      [t('clientBonds.interest_rate'), b.coupon_rate === null ? '---' : formatNumber(b.coupon_rate) + ' % p.a.'],
      [t('clientBonds.coupon_payout'), b.coupon_frequency_label ?? '---'],
      [t('clientBonds.par_value'), b.nominal_value === null ? '---' : formatAmount(b.nominal_value, b.currency)],
    ],
    [
      [t('clientBonds.issue_date'), date(b.issue_date)],
      [t('clientBonds.maturity'), date(b.maturity_date)],
      [t('clientBonds.currency'), b.currency ?? '---'],
      [t('clientBonds.withholding_tax'), WITHHOLDING_TAX_PCT + ' %'],
    ],
  ];
});

/** Počet dní do konce prodeje (kalendářní dny, „dnes“ = 0), nebo null. */
const saleDaysLeft = computed(() => {
  if (!bond.value?.sale_to || bond.value.sale_state !== 'ON_SALE') {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(bond.value.sale_to + 'T00:00:00') - today) / 86400000);
  return days >= 0 ? days : null;
});

/** Volný objem v měně (volné kusy × jmenovitá hodnota) a podíl upsaného objemu pro ukazatel. */
const freeVolume = computed(() => {
  const b = bond.value;
  if (!b || b.free_pieces === null || b.free_pieces === undefined || b.nominal_value === null) {
    return null;
  }
  return b.free_pieces * b.nominal_value;
});
const subscribedPct = computed(() => {
  const b = bond.value;
  if (!b?.emission_volume || b.subscribed_volume === null || b.subscribed_volume === undefined) {
    return null;
  }
  return Math.min(100, Math.max(0, (b.subscribed_volume / b.emission_volume) * 100));
});

/** Řádky karty Stav. */
const stateRows = computed(() => {
  const b = bond.value;
  if (!b) {
    return [];
  }
  const rows = [[t('clientBonds.sale_to'), b.sale_to ? date(b.sale_to) + (saleDaysLeft.value !== null ? ' ' + t('clientBonds.days_left', {days: saleDaysLeft.value}) : '') : '---']];
  if (!isAdvisor.value && b.owned_quantity) {
    rows.push([t('clientBonds.owned_nominal'), formatAmount(b.owned_quantity * (b.nominal_value ?? 0), b.currency)]);
  }
  if (isAdvisor.value) {
    rows.push([t('clientBonds.my_clients_with_bond'), t('clientBonds.clients_count_value', {count: b.clients_count ?? 0})]);
  }
  return rows;
});

const canBuy = computed(() => !isAdvisor.value && bond.value?.is_available);

/** Plán výplat podle přepínače Budoucí / Všechny. */
const schedule = computed(() => {
  const rows = bond.value?.payment_schedule ?? [];
  return scheduleFilter.value === 'future' ? rows.filter(row => !row.is_paid) : rows;
});
const isIndicative = computed(() => bond.value?.payment_schedule_type === 'INDICATIVE');
</script>

<template>
  <div>
    <div v-if="loading" class="text-center my-4">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">{{ t('clientBonds.loading') }}</span></div>
    </div>
    <div v-else-if="notFound" class="card">
      <div class="card-body text-center text-muted">{{ t('clientBonds.not_found') }}</div>
    </div>
    <template v-else>
        <!-- Parametry a Stav vedle sebe -->
        <div class="row g-3">
          <div class="col-12 col-lg-6">
            <div class="card shadow h-100">
              <div class="card-header"><div class="card-header__title">{{ t('clientBonds.params') }}</div></div>
              <div class="card-body">
                <p v-if="bond.client_description" class="mb-3">{{ bond.client_description }}</p>
                <div class="row">
                  <div v-for="(column, columnIndex) of paramColumns" :key="columnIndex" class="col-12 col-md-6 info-table">
                    <div v-for="[label, value] of column" :key="label" class="info-table__row row">
                      <span class="info-table__label col-12">{{ label }}</span>
                      <span class="info-table__value col-12">{{ value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="card shadow h-100">
              <div class="card-header"><div class="card-header__title">{{ t('clientBonds.state_card') }}</div></div>
              <div class="card-body d-flex flex-column gap-3">
                <div class="d-flex flex-wrap gap-1">
                  <span class="badge" :class="bond.sale_state === 'ON_SALE' ? 'bg-success' : 'bg-secondary'">{{ t('bonds.sale_states.' + bond.sale_state) }}</span>
                  <span v-if="!isAdvisor && bond.owned_quantity" class="badge bg-light text-dark">{{ t('clientBonds.owned_badge', {count: formatNumber(bond.owned_quantity, 0)}) }}</span>
                  <span v-if="saleDaysLeft !== null && saleDaysLeft <= 30" class="badge bg-warning text-dark">{{ t('clientBonds.sale_ends_in', {days: saleDaysLeft}) }}</span>
                </div>
                <div class="info-table">
                  <div v-for="[label, value] of stateRows" :key="label" class="info-table__row row">
                    <span class="info-table__label col-12">{{ label }}</span>
                    <span class="info-table__value col-12">{{ value }}</span>
                  </div>
                  <!-- volný objem s ukazatelem upsaného objemu (bez objemu z ESO „---“) -->
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('clientBonds.free_volume') }}</span>
                    <span class="info-table__value col-12">
                      {{ freeVolume === null ? '---' : formatAmount(freeVolume, bond.currency, 0) }}
                      <div v-if="subscribedPct !== null" class="progress mt-2" style="height: 6px;">
                        <div class="progress-bar" role="progressbar" :style="{width: subscribedPct + '%'}"></div>
                      </div>
                    </span>
                  </div>
                </div>
                <div v-if="canBuy && !embedded" class="mt-auto">
                  <button type="button" class="btn btn-primary w-100" @click="purchaseOpen = true">{{ bond.owned_quantity ? t('clientBonds.buy_more') : t('clientBonds.buy') }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Plán výplat -->
        <div class="page-section mt-5">
          <div class="page-section__heading"><div class="page-section__title">{{ t('clientBonds.payment_schedule') }}</div></div>
          <div class="page-section__content">
            <ul class="nav--tabs nav nav--pills mb-3" role="tablist">
              <li class="nav-item col-6" role="presentation">
                <button type="button" class="nav-link" :class="{active: scheduleFilter === 'future'}" role="tab" @click="scheduleFilter = 'future'">{{ t('clientBonds.schedule_future') }}</button>
              </li>
              <li class="nav-item col-6" role="presentation">
                <button type="button" class="nav-link" :class="{active: scheduleFilter === 'all'}" role="tab" @click="scheduleFilter = 'all'">{{ t('clientBonds.schedule_all') }}</button>
              </li>
            </ul>
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                    <tr>
                      <th>{{ t('clientBonds.date') }}<div class="table__sub-value">{{ t('clientBonds.state') }}</div></th>
                      <th>{{ t('clientBonds.period') }}</th>
                      <th>
                        {{ isIndicative ? t('clientBonds.coupon_gross_piece') : t('clientBonds.coupon_gross') }}
                        <div class="table__sub-value">{{ isIndicative ? t('clientBonds.coupon_net_piece') : t('clientBonds.coupon_net') }}</div>
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row of schedule" :key="row.payment_date">
                      <td>
                        {{ formatDate(row.payment_date, 'd.m.Y') }}
                        <div class="table__sub-value">
                          <span class="badge" :class="row.is_paid ? 'bg-success' : 'bg-light text-dark'">{{ row.is_paid ? t('clientBonds.schedule_paid') : t('clientBonds.schedule_planned') }}</span>
                          <span v-if="row.type === 'COUPON_PRINCIPAL'" class="ms-1">{{ t('clientBonds.schedule_principal') }}</span>
                        </div>
                      </td>
                      <!-- úrokové období kupónu (od–do) z plánu ESO, u orientačního plánu dopočtené z frekvence -->
                      <td>{{ row.period_from && row.period_to ? formatDate(row.period_from, 'd.m.Y') + ' – ' + formatDate(row.period_to, 'd.m.Y') : '---' }}</td>
                      <td>
                        {{ formatAmount(row.amount_gross, row.currency) }}
                        <div class="table__sub-value">{{ row.amount_net === null || row.amount_net === undefined ? '---' : formatAmount(row.amount_net, row.currency) }}</div>
                      </td>
                    </tr>
                    <tr v-if="!schedule.length">
                      <td colspan="3" class="table__empty-td"><div class="table__empty-text">{{ t('clientBonds.no_schedule') }}</div></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dokumenty k emisi -->
        <div class="page-section mt-5">
          <div class="page-section__heading">
            <div class="page-section__title">{{ t('clientBonds.documents') }}</div>
            <div class="page-section__subtitle">{{ t('clientBonds.documents_subtitle') }}</div>
          </div>
          <div class="page-section__content">
            <div class="card shadow">
              <div class="card-body">
                <div class="table-container">
                  <table class="table table--last-to-right table--cells-py-0">
                    <thead>
                    <tr>
                      <th>{{ t('clientBonds.doc_name') }}</th>
                      <th>{{ t('clientBonds.version') }}</th>
                      <th>{{ t('clientBonds.valid_from') }}</th>
                      <th>{{ t('clientBonds.required') }}</th>
                      <th>{{ t('clientBonds.actions') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="document of bond.documents" :key="document.id">
                      <td>{{ document.name }}</td>
                      <td>{{ document.version ?? '---' }}</td>
                      <td>{{ document.valid_from ? formatDate(document.valid_from, 'd.m.Y') : '---' }}</td>
                      <!-- všechny dokumenty k emisi jsou povinné -->
                      <td>{{ t('clientBonds.yes') }}</td>
                      <td class="text-nowrap">
                        <!-- náhled v prohlížeči PDF aplikace (download_url soubor stahuje); stažení přes uuid funguje i v mobilní aplikaci -->
                        <!-- ve vloženém detailu (modal v onboardingu) v nové záložce, aby se onboarding nezavřel -->
                        <a v-if="embedded" :href="router.resolve({name: 'PDFViewer', params: {uuid: document.file.uuid}}).href" target="_blank" class="btn btn-link" :title="t('clientBonds.preview')"><i class="mdi mdi-eye"/></a>
                        <router-link v-else :to="{name: 'PDFViewer', params: {uuid: document.file.uuid}}" class="btn btn-link" :title="t('clientBonds.preview')"><i class="mdi mdi-eye"/></router-link>
                        <DownloadButtonComponent :uuid="document.file.uuid" :downloadUrl="document.file.download_url"/>
                      </td>
                    </tr>
                    <tr v-if="!bond.documents.length">
                      <td colspan="5" class="table__empty-td"><div class="table__empty-text">{{ t('clientBonds.no_documents') }}</div></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
    </template>
    <BondPurchaseOverlay v-if="purchaseOpen && bond" :bond="bond" @close="onPurchaseClose"/>
  </div>
</template>
